/**
 * Symptoms API
 */
import { apiClient } from '../api-client';
import type { SymptomLog, SymptomsHistoryResponse } from './types';

export const symptomsApi = {
  /**
   * Get current patient user's symptom history (no patient_id needed)
   */
  getMySymptomHistory: async (days: number = 30): Promise<SymptomsHistoryResponse> => {
    return apiClient.get(`/symptoms/me/symptoms`, { days });
  },

  /**
   * Get common triggers for current patient user
   */
  getMyCommonTriggers: async (): Promise<string[]> => {
    return apiClient.get(`/symptoms/me/symptoms/triggers`);
  },

  /**
   * Log current user's symptoms (for patients)
   */
  logMySymptoms: async (
    data: {
      patient_id: number;
      date: string;
      symptoms: {
        breathlessness: number;
        energy_level: number;
        cough: number;
        sleep_quality: number;
        chest_pain: number;
        fatigue: number;
      };
      triggers?: string[];
      additional_notes?: string;
    }
  ): Promise<SymptomLog> => {
    return apiClient.post(`/symptoms/me/symptoms`, data);
  },

  /**
   * Record symptom for current patient (matches backend schema)
   */
  recordMySymptom: async (data: {
    breathlessness: number;
    energy_level: number;
    cough: number;
    sleep_quality: number;
    chest_pain: number;
    fatigue: number;
    triggers?: string[];
    additional_notes?: string;
  }): Promise<SymptomLog> => {
    // Get patient_id from auth context - backend will use it from JWT
    const today = new Date().toISOString().split('T')[0];
    
    return apiClient.post(`/symptoms/me`, {
      patient_id: 1, // Backend will override this with actual patient_id from JWT
      date: today,
      symptoms: {
        breathlessness: data.breathlessness,
        energy_level: data.energy_level,
        cough: data.cough,
        sleep_quality: data.sleep_quality,
        chest_pain: data.chest_pain,
        fatigue: data.fatigue,
      },
      triggers: data.triggers || [],
      additional_notes: data.additional_notes || null,
    });
  },

  /**
   * Log patient symptoms (with patient ID in URL)
   */
  logSymptoms: async (
    patientId: number,
    data: {
      patient_id: number;
      date: string;
      symptoms: {
        breathlessness: number;
        energy_level: number;
        cough: number;
        sleep_quality: number;
        chest_pain: number;
        fatigue: number;
      };
      triggers?: string[];
      additional_notes?: string;
    }
  ): Promise<SymptomLog> => {
    return apiClient.post(`/symptoms/patients/${patientId}/symptoms`, data);
  },

  /**
   * Get symptom history
   */
  getSymptomHistory: async (
    patientId: number,
    days: number = 30
  ): Promise<SymptomsHistoryResponse> => {
    return apiClient.get(`/symptoms/patients/${patientId}/symptoms`, { days });
  },

  /**
   * Get common triggers
   */
  getCommonTriggers: async (patientId: number): Promise<{ triggers: string[] }> => {
    return apiClient.get(`/symptoms/patients/${patientId}/symptoms/triggers`);
  },

  /**
   * ✅ NEW: Get single symptom log by ID
   */
  getSymptomById: async (symptomId: number): Promise<SymptomLog> => {
    return apiClient.get(`/symptoms/me/symptoms/${symptomId}`);
  },

  /**
   * ✅ NEW: Update symptom log
   */
  updateMySymptom: async (symptomId: number, data: any): Promise<SymptomLog> => {
    return apiClient.put(`/symptoms/me/symptoms/${symptomId}`, data);
  },

  /**
   * ✅ NEW: Delete symptom log
   */
  deleteMySymptom: async (symptomId: number): Promise<{ status: string; message: string; symptom_id: number }> => {
    return apiClient.delete(`/symptoms/me/symptoms/${symptomId}`);
  },

  // ==================== DOCTOR ENDPOINTS ====================

  /**
   * Get all patients with unreviewed symptoms (Doctor/PA only)
   */
  getUnreviewedSymptoms: async (days: number = 30): Promise<{
    patients_with_symptoms: Array<{
      patient_id: number;
      patient_name: string;
      patient_age: number;
      patient_gender: string;
      patient_umr: string;
      last_reported: string;
      unreviewed_count: number;
      recent_symptoms: string[];
      patient_notes: string | null;
      symptom_logs: SymptomLog[];
    }>;
    total_patients: number;
  }> => {
    return apiClient.get(`/symptoms/doctor/unreviewed`, { days });
  },

  /**
   * Mark all unreviewed symptoms for a patient as reviewed (Doctor/PA only)
   */
  markSymptomsAsReviewed: async (patientId: number): Promise<{
    status: string;
    message: string;
    reviewed_count: number;
    reviewed_by: number;
    reviewed_date: string;
  }> => {
    return apiClient.post(`/symptoms/doctor/review/${patientId}`, {});
  },
};